import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Modal, Alert, Form } from "antd";
import { BACKEND_URL } from "../../../config/config";
import { NotificationContext } from "../../context/NotificationWrapper";
import { DeclarationContext } from "../../context/DeclarationWrapper";
import { AuthContext } from "../../context/AuthProviderWrapper";
import { renderCheckboxOrHidden } from "../../helper-function/helperFunction";
import { useDeclarationForm } from "../../../hooks/useDeclarationForm";
import { useZeroAmountValidation } from "../../../hooks/useZeroAmountValidation";
import ZeroAmountConfirmationModal from "../../misc/ZeroAmountConfirmationModal";
import DeclarationFormTable from "../../common/DeclarationFormTable";
import {
  FooterEditDeclarationModal,
  TitleEditDeclarationModal,
} from "./EditDeclaration/chooseTiitleAndHeader";

const monthsData = [
  { value: "1", label: "Janvier" },
  { value: "2", label: "Février" },
  { value: "3", label: "Mars" },
  { value: "4", label: "Avril" },
  { value: "5", label: "Mai" },
  { value: "6", label: "Juin" },
  { value: "7", label: "Juillet" },
  { value: "8", label: "Août" },
  { value: "9", label: "Septembre" },
  { value: "10", label: "Octobre" },
  { value: "11", label: "Novembre" },
  { value: "12", label: "Décembre" },
];

const EditDeclarationModal = ({ open, onCancel, record, mode, isPreviewMode = false, isAnnual = false, isSemiAnnual = false, semester = null }) => {
  const navigate = useNavigate();
  const { user, isAdmin } = useContext(AuthContext);
  const { openNotification } = useContext(NotificationContext);
  const { updateDeclaration, isZeroAmount, isZeroWorkForce, updateDsnFile, refreshAvailablePeriods } = useContext(DeclarationContext);
  const backendUrl = BACKEND_URL;

  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [showZeroAmountModal, setShowZeroAmountModal] = useState(false);
  const [conditionCheckbox, setConditionCheckbox] = useState(false);
  const [validatedDeclarationData, setValidatedDeclarationData] = useState(null);
  const [currentSemester, setCurrentSemester] = useState(record?.semester || null);

  // Use the useDeclarationForm hook for all form logic
  const {
    salary,
    taxes,
    errors,
    isInputLoading,
    setErrors,
    handleFormValuesChange,
    handleMonthChange,
    handleYearChange,
    updateSalary,
    updateTaxes,
    abortControllerRef,
  } = useDeclarationForm({
    initialSalary: {
      salaryWorker: record?.salaryWorker,
      salaryEtam: record?.salaryEtam,
      salaryExecutive: record?.salaryExecutive,
      workForce: record?.workForce,
      month: record?.month,
      year: record?.year,
      agreeOptionalWorker: record?.agreeOptionalWorker,
      id: record?.id, // IMPORTANT: Include ID for edit mode validation
      semester: record?.semester,
    },
    initialTaxes: {
      feeExecutive: record?.feeExecutive || 0,
      feeEtam: record?.feeEtam || 0,
      feeWorker: record?.feeWorker || 0,
      tvaExecutive: record?.tvaExecutive || 0,
      tvaEtam: record?.tvaEtam || 0,
      tvaWorker: record?.tvaWorker || 0,
      feeAmount: record?.feeAmount || 0,
    },
    membershipCode: record?.membershipCode,
    autoCalculate: true,
    form: form,
  });

  // Use the useZeroAmountValidation hook for validation logic
  const {
    validateOptionalWorkerAgreement,
    ERROR_MESSAGES,
  } = useZeroAmountValidation();

  // Initialize form fields when record changes or modal opens
  useEffect(() => {
    if (record && open) {
      // Normaliser le mois : retirer le zéro en tête si présent
      const normalizedMonth = record.month ? String(parseInt(record.month, 10)) : null;

      // Normaliser l'année : s'assurer que c'est une string
      const normalizedYear = record.year ? String(record.year) : null;

      // Synchroniser le semester avec record
      if (record.semester !== undefined) {
        setCurrentSemester(record.semester);
      }

      form.setFieldsValue({
        ...record,
        month: normalizedMonth,
        year: normalizedYear,
        semester: record.semester,
        salaryExecutiveFee: record?.feeExecutiveLocale,
        salaryExecutiveTva: record?.tvaExecutiveLocale,
        salaryEtamFee: record?.feeEtamLocale,
        salaryEtamTva: record?.tvaEtamLocale,
        salaryWorkerFee: record?.feeWorkerLocale,
        salaryWorkerTva: record?.tvaWorkerLocale,
        feeAmount: record?.feeAmountLocale,
      });

      // Update salary state when record changes
      updateSalary({
        salaryWorker: record.salaryWorker,
        salaryEtam: record.salaryEtam,
        salaryExecutive: record.salaryExecutive,
        workForce: record.workForce,
        month: normalizedMonth,
        year: normalizedYear,
        agreeOptionalWorker: record.agreeOptionalWorker,
        id: record.id,
        semester: record.semester,
      });
    }
  }, [record, open]);

  // Update checkbox condition
  useEffect(() => {
    const condition = renderCheckboxOrHidden(user, salary);
    setConditionCheckbox(condition);
  }, [user, salary]);

  /**
   * Close modal and reset state
   */
  const onCloseModal = () => {
    setErrors([]);
    setValidatedDeclarationData(null);
    onCancel();
    form.resetFields();
    if (form && record) {
      form.setFieldsValue({
        salaryExecutiveFee: record?.feeExecutiveLocale,
        salaryExecutiveTva: record?.tvaExecutiveLocale,
        salaryEtamFee: record?.feeEtamLocale,
        salaryEtamTva: record?.tvaEtamLocale,
        salaryWorkerFee: record?.feeWorkerLocale,
        salaryWorkerTva: record?.tvaWorkerLocale,
      });
    }
  };

  /**
   * Handler pour changement de semestre
   */
  const handleSemesterChange = (value) => {
    setCurrentSemester(value);
    updateSalary({ semester: value });
    form?.setFieldsValue({ semester: value });
  };

  /**
   * Main form submission handler with 4-case validation
   */
  const onFinish = () => {
    if (isSubmitting) return;

    // Reset errors
    setErrors([]);

    const totalAmount = taxes?.feeAmount || 0;
    const workForce = parseInt(salary?.workForce || '0', 10);
    const isOptionalWorkerAgreed = salary?.agreeOptionalWorker || false;

    // Use context methods to check amounts and workforce
    const hasZeroAmount = isZeroAmount(totalAmount);
    const hasZeroWorkForce = isZeroWorkForce(workForce);

    // 4-case validation
    if (hasZeroAmount && hasZeroWorkForce) {
      // Case 1: Attestation (montant = 0 AND effectifs = 0)
      handleAttestationCase();
    } else if (hasZeroAmount && !hasZeroWorkForce) {
      // Case 3: Error (montant = 0 but effectifs > 0)
      setErrors([ERROR_MESSAGES.ZERO_AMOUNT_WITH_WORKFORCE]);
    } else if (!hasZeroAmount && hasZeroWorkForce) {
      // Case 4: Error (montant > 0 but effectifs = 0)
      setErrors([ERROR_MESSAGES.POSITIVE_AMOUNT_WITHOUT_WORKFORCE]);
    } else {
      // Case 2: Normal declaration (montant > 0 AND effectifs > 0)
      // Validate optional worker checkbox if needed
      const checkboxError = validateOptionalWorkerAgreement(
        workForce,
        isOptionalWorkerAgreed,
        conditionCheckbox
      );
      if (checkboxError) {
        setErrors([checkboxError]);
        return;
      }
      handleSubmit();
    }
  };

  /**
   * Handle attestation case (Case 1)
   * Always show ZeroAmountModal for attestations (both creation and edition)
   */
  const handleAttestationCase = () => {
    validateBackendThenShowModal();
  };

  /**
   * Validate with backend then show zero amount modal
   */
  const validateBackendThenShowModal = () => {
    setIsSubmitting(true);
    setIsLoading(true);
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setErrors([]);

    const requestBody = buildRequestBody();

    axios
      .post(`${backendUrl}/api/declaration/check-validity`, requestBody, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        signal: abortControllerRef.current?.signal,
      })
      .then((response) => {
        setValidatedDeclarationData(response.data.data);
        setShowZeroAmountModal(true);
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        handleApiError(error);
      })
      .finally(() => {
        setIsLoading(false);
        setIsSubmitting(false);
      });
  };

  /**
   * Handle zero amount modal confirmation
   * Distinguishes between creation workflow (update state only) and edition workflow (API call)
   */
  const handleZeroAmountConfirm = (dsnFile) => {
    setShowZeroAmountModal(false);

    if (isPreviewMode || !record?.id) {
      // Mode CRÉATION : mettre à jour le state uniquement
      // La création réelle en DB se fera plus tard dans DeclarationSignature
      updateDsnFile(dsnFile);
      if (validatedDeclarationData) {
        updateDeclaration(validatedDeclarationData);
      }
      onCloseModal();
    } else {
      // Mode ÉDITION : appel API pour créer la demande de modification
      handleSubmitModificationToZero(dsnFile);
    }
  };

  /**
   * Handle zero amount modal cancellation
   */
  const handleZeroAmountCancel = () => {
    setShowZeroAmountModal(false);
    setValidatedDeclarationData(null);
  };

  /**
   * Submit modification to zero (attestation with DSN file)
   */
  const handleSubmitModificationToZero = (dsnFile) => {
    setIsSubmitting(true);
    setIsLoading(true);
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setErrors([]);

    const formData = new FormData();
    formData.append('dsnFile', dsnFile);

    axios
      .post(
        `${backendUrl}/api/attestation-request/request-modification/${record?.id}`,
        formData,
        {
          withCredentials: true,
          headers: {
            "Content-Type": "multipart/form-data",
          },
          signal: abortControllerRef.current?.signal,
        }
      )
      .then(() => {
        openNotification({
          type: 'success',
          message: 'Demande de modification soumise',
          description: 'Votre demande de modification vers attestation a été envoyée avec succès. Elle sera examinée par l\'APAS-BTP.',
        });
        refreshAvailablePeriods();
        onCloseModal();
        navigate("/tableDeclaration");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        handleApiError(error);
        openNotification({
          type: 'error',
          message: 'Erreur',
          description: error.response?.data?.message || 'Une erreur est survenue lors de la soumission de votre demande.',
        });
      })
      .finally(() => {
        setIsLoading(false);
        setIsSubmitting(false);
      });
  };

  /**
   * Standard submit handler
   */
  const handleSubmit = () => {
    setIsSubmitting(true);
    setIsLoading(true);
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    setErrors([]);

    const requestBody = buildRequestBody();

    // Preview mode: validate and update state only
    if (isPreviewMode) {
      axios
        .post(`${backendUrl}/api/declaration/check-validity`, requestBody, {
          withCredentials: true,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          signal: abortControllerRef.current?.signal,
        })
        .then((res) => {
          openNotification("Succes", "Déclaration modifiée", "success");
          refreshAvailablePeriods();
          onCloseModal();
          return res.data.data;
        })
        .then((res) => {
          // Préserver isAnnual et semester lors de la mise à jour du contexte
          updateDeclaration({
            ...res,
            isAnnual: isAnnual,
            isSemiAnnual: isSemiAnnual,
            semester: isSemiAnnual ? currentSemester : null,
          });
        })
        .catch((error) => {
          if (error.name === "AbortError") return;
          handleApiError(error);
        })
        .finally(() => {
          setIsLoading(false);
          setIsSubmitting(false);
        });
    } else {
      // Real edit mode: show confirmation modal
      if (!record?.id) {
        setErrors(["Erreur : aucune déclaration à modifier"]);
        setIsLoading(false);
        setIsSubmitting(false);
      } else {
        setConfirmModalVisible(true);
        setIsLoading(false);
        setIsSubmitting(false);
      }
    }
  };

  /**
   * Handle edit confirmation
   */
  const handleConfirmEdit = () => {
    setConfirmModalVisible(false);
    setIsSubmitting(true);
    setIsLoading(true);

    const requestBody = buildRequestBody();
    axiosEditDeclaration(requestBody);
  };

  /**
   * Handle edit cancellation
   */
  const handleCancelEdit = () => {
    setConfirmModalVisible(false);
    setIsLoading(false);
    setIsSubmitting(false);
  };

  /**
   * Submit edit to backend
   */
  const axiosEditDeclaration = (requestBody) => {
    axios
      .post(`${backendUrl}/api/declaration/submit`, requestBody, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        signal: abortControllerRef.current?.signal,
      })
      .then(() => {
        openNotification("Succes", "Déclaration modifiée", "success");
        refreshAvailablePeriods();
        setErrors([]);
        onCloseModal();
        navigate("/tableDeclaration");
      })
      .catch((error) => {
        if (error.name === "AbortError") return;
        handleApiError(error);
        openNotification(
          "Erreur",
          "Les données soumises sont incorrectes",
          "error"
        );
      })
      .finally(() => {
        setIsLoading(false);
        setIsSubmitting(false);
      });
  };

  /**
   * Build request body for API calls
   */
  const buildRequestBody = () => {
    const body = {
      salaryWorker: salary?.salaryWorker,
      salaryEtam: salary?.salaryEtam,
      salaryExecutive: salary?.salaryExecutive,
      workForce: parseInt(salary.workForce, 10),
      month: salary?.month,
      year: salary?.year,
      agreeOptionalWorker: salary?.agreeOptionalWorker,
      membershipCode: record?.membershipCode,
      id: salary?.id,
      isAnnual: isAnnual,
    };

    // N'envoyer semester que pour les déclarations semestrielles
    if (isSemiAnnual && currentSemester) {
      body.semester = currentSemester;
    }

    return body;
  };

  /**
   * Handle API errors consistently
   */
  const handleApiError = (error) => {
    if (error.response && error.response.data) {
      if (error.response.data.description) {
        setErrors([error.response.data.description]);
      } else if (Array.isArray(error.response.data.data)) {
        setErrors(error.response.data.data);
      } else if (error.response.data.data) {
        setErrors([error.response.data.data]);
      } else {
        setErrors([error.response.data.message || "Une erreur est survenue"]);
      }
    } else {
      setErrors(["Une erreur inattendue s'est produite"]);
    }
    console.error("error", error);
  };

  return (
    <Modal
      title={<TitleEditDeclarationModal record={record} />}
      open={open}
      onCancel={onCloseModal}
      footer={null}
      destroyOnClose={true}
      width={800}
    >
      {errors?.length > 0 && (
        <Alert
          type="error"
          message={errors.map((el, index) => <p key={index}>{el}</p>)}
        />
      )}

      <Form
        form={form}
        name="editDeclaration"
        onValuesChange={handleFormValuesChange}
        onFinish={onFinish}
        scrollToFirstError
        initialValues={record}
      >
        <DeclarationFormTable
          mode={mode}
          formType="antd"
          salary={salary}
          taxes={taxes}
          isInputLoading={isInputLoading}
          conditionCheckbox={conditionCheckbox}
          onMonthChange={handleMonthChange}
          onYearChange={handleYearChange}
          onSemesterChange={handleSemesterChange}
          form={form}
          isAnnual={isAnnual}
          isSemiAnnual={isSemiAnnual}
          semester={currentSemester}
        />

        <div>
          <FooterEditDeclarationModal
            mode={mode}
            isLoading={isLoading}
            onCloseModal={onCloseModal}
            record={record}
            isAdmin={isAdmin}
          />
        </div>
      </Form>

      {/* Confirmation modal for edits */}
      <Modal
        title="Confirmation de modification"
        open={confirmModalVisible}
        onOk={handleConfirmEdit}
        onCancel={handleCancelEdit}
        centered={true}
        okText="Valider"
        cancelText="Annuler"
        okButtonProps={{ style: { backgroundColor: '#ee463b', borderColor: '#ee463b' } }}
        animation={{
          enter: {
            opacity: [0, 1],
            transform: ['translateY(-50px)', 'translateY(0)']
          },
          leave: {
            opacity: [1, 0],
            transform: ['translateY(0)', 'translateY(-50px)']
          }
        }}
      >
        <div>
          <p style={{ fontWeight: 'bold', fontSize: 20 }}>
            Période : {(() => {
              if (isAnnual) {
                return `Année ${salary?.year || '(Année non définie)'}`;
              } else if (isSemiAnnual) {
                const semesterLabel = currentSemester === 1 ? 'S1 (Janvier - Juin)' : 'S2 (Juillet - Décembre)';
                return `${semesterLabel} ${salary?.year || '(Année non définie)'}`;
              } else {
                // Convertir la valeur du mois en nombre pour éliminer le zéro en tête
                const monthNumber = salary?.month ? parseInt(salary.month, 10).toString() : null;
                const monthLabel = monthNumber
                  ? monthsData.find(m => m.value === monthNumber)?.label || '(Mois inconnu)'
                  : '(Mois non défini)';

                return `${monthLabel} ${salary?.year || '(Année non définie)'}`;
              }
            })()}
          </p>
          <p style={{ fontWeight: 'bold', fontSize: 20 }}>
            Montant dû : {taxes?.feeAmount ? `${taxes.feeAmount} €` : form.getFieldValue('feeAmount')}
          </p>
        </div>
      </Modal>

      {/* Zero amount confirmation modal */}
      <ZeroAmountConfirmationModal
        visible={showZeroAmountModal}
        onConfirm={handleZeroAmountConfirm}
        onCancel={handleZeroAmountCancel}
        month={salary.month}
        year={salary.year}
        isAnnual={isAnnual}
        isSemiAnnual={isSemiAnnual}
        semester={currentSemester}
      />
    </Modal>
  );
};

export default EditDeclarationModal;
